import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  fetchTrendArticles,
  fetchTrendDiscoveryCandidates,
  fetchTrendReport,
  fetchTrendSourceItems,
} from '../../service';
import { DISPLAY_FONT, TREND_COLORS } from '../../trendTheme';
import type { TrendArticle, TrendPipelineRun, TrendReport } from '../../types';
import { getRunCategoryModeLabel } from '../adminHelpers';

type ModuleView = 'overview' | 'editorial';

type PostRunNextStepsPanelProps = {
  run: TrendPipelineRun | null;
  canReview: boolean;
  canManageSources: boolean;
  onControlCenterTab: (tab: number) => void;
  onNavigateModule?: (view: ModuleView) => void;
};

type PostRunSummary = {
  report: TrendReport | null;
  articles: TrendArticle[];
  pendingDiscoveryCount: number;
  needsValidationDiscoveryCount: number;
  sourceReviewCount: number;
  sourceFailedCount: number;
};

const EMPTY_SUMMARY: PostRunSummary = {
  report: null,
  articles: [],
  pendingDiscoveryCount: 0,
  needsValidationDiscoveryCount: 0,
  sourceReviewCount: 0,
  sourceFailedCount: 0,
};

function formatDateTime(value?: string | null): string {
  if (!value) return 'okänd tid';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'okänd tid';
  return parsed.toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
}

function reportStatusLabel(report: TrendReport | null): string {
  if (!report) return 'Rapportutkast saknas';
  if (report.status === 'published') return 'Aktiv rapport';
  if (report.status === 'review') return 'Redo för granskning';
  return 'Utkast';
}

function actionTone(count: number, fallback: 'success' | 'info' | 'warning' = 'info'): 'success' | 'info' | 'warning' {
  if (count > 0) return 'warning';
  return fallback;
}

function ActionCard({
  icon,
  title,
  description,
  chip,
  tone = 'info',
  buttonLabel,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  chip: string;
  tone?: 'success' | 'info' | 'warning';
  buttonLabel: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const accent = tone === 'success' ? '#2e7d32' : tone === 'warning' ? '#ed6c02' : TREND_COLORS.navy;
  const chipColor = tone === 'success' ? 'success' : tone === 'warning' ? 'warning' : 'info';

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Box
        sx={{
          height: '100%',
          p: 2,
          borderRadius: 2,
          bgcolor: TREND_COLORS.sectionBg,
          border: `1px solid ${TREND_COLORS.divider}`,
          borderLeft: `4px solid ${accent}`,
        }}
      >
        <Stack spacing={1.5} sx={{ height: '100%' }}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box sx={{ color: accent, pt: 0.15 }}>{icon}</Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: TREND_COLORS.navy }}>
                  {title}
                </Typography>
                <Chip size="small" color={chipColor} variant={tone === 'success' ? 'filled' : 'outlined'} label={chip} />
              </Stack>
              <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mt: 0.75, lineHeight: 1.65 }}>
                {description}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ mt: 'auto' }}>
            <Button
              size="small"
              variant={tone === 'warning' ? 'contained' : 'outlined'}
              disabled={disabled || !onClick}
              onClick={onClick}
              endIcon={<OpenInNewOutlinedIcon sx={{ fontSize: '0.95rem' }} />}
              sx={tone === 'warning'
                ? { bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } }
                : { borderColor: TREND_COLORS.divider, color: TREND_COLORS.navy }}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Grid>
  );
}

export default function PostRunNextStepsPanel({
  run,
  canReview,
  canManageSources,
  onControlCenterTab,
  onNavigateModule,
}: PostRunNextStepsPanelProps) {
  const shouldShow = !!run
    && run.status === 'completed'
    && run.scope === 'live'
    && run.profile === 'full'
    && run.publishMode !== 'preview';
  const [summary, setSummary] = useState<PostRunSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shouldShow || !run?._id) {
      setSummary(EMPTY_SUMMARY);
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const reportPromise = run.reportId
      ? fetchTrendReport(run.reportId).catch(() => null)
      : Promise.resolve(null);
    const articlesPromise = canReview
      ? fetchTrendArticles({ fromRun: run._id, qualityScope: 'all' }).catch(() => [])
      : Promise.resolve([]);
    const pendingDiscoveryPromise = canManageSources
      ? fetchTrendDiscoveryCandidates({ status: 'pending', limit: 1 }).catch(() => ({ items: [], total: 0 }))
      : Promise.resolve({ items: [], total: 0 });
    const needsValidationDiscoveryPromise = canManageSources
      ? fetchTrendDiscoveryCandidates({ status: 'needs_validation', limit: 1 }).catch(() => ({ items: [], total: 0 }))
      : Promise.resolve({ items: [], total: 0 });
    const sourceReviewPromise = canManageSources
      ? fetchTrendSourceItems({ status: 'review', limit: 1 }).catch(() => ({ items: [], total: 0 }))
      : Promise.resolve({ items: [], total: 0 });
    const sourceFailedPromise = canManageSources
      ? fetchTrendSourceItems({ status: 'failed', limit: 1 }).catch(() => ({ items: [], total: 0 }))
      : Promise.resolve({ items: [], total: 0 });

    void Promise.all([
      reportPromise,
      articlesPromise,
      pendingDiscoveryPromise,
      needsValidationDiscoveryPromise,
      sourceReviewPromise,
      sourceFailedPromise,
    ])
      .then(([report, articles, pendingDiscovery, needsValidationDiscovery, sourceReview, sourceFailed]) => {
        if (!active) return;
        setSummary({
          report,
          articles,
          pendingDiscoveryCount: pendingDiscovery.total,
          needsValidationDiscoveryCount: needsValidationDiscovery.total,
          sourceReviewCount: sourceReview.total,
          sourceFailedCount: sourceFailed.total,
        });
      })
      .catch(() => {
        if (!active) return;
        setSummary(EMPTY_SUMMARY);
        setError('Kunde inte ladda efter-körningsstegen. Prova att ladda om Kontrollcenter.');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canManageSources, canReview, run, shouldShow]);

  const articleCounts = useMemo(() => {
    const review = summary.articles.filter((article) => (
      article.qualityStatus === 'requires_review' || article.status === 'review'
    )).length;
    const blocked = summary.articles.filter((article) => article.qualityStatus === 'blocked').length;
    const publishable = summary.articles.filter((article) => article.qualityStatus === 'publishable').length;
    return { review, blocked, publishable };
  }, [summary.articles]);

  if (!shouldShow || !run) return null;

  const discoveryQueueCount = summary.pendingDiscoveryCount + summary.needsValidationDiscoveryCount;
  const sourceIssueCount = summary.sourceReviewCount + summary.sourceFailedCount;
  const reportIsPublished = summary.report?.status === 'published';

  return (
    <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography
              variant="overline"
              sx={{ fontFamily: DISPLAY_FONT, letterSpacing: '0.18em', fontWeight: 800, color: TREND_COLORS.olive }}
            >
              Nästa steg
            </Typography>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.25rem', fontWeight: 800, color: TREND_COLORS.navy }}>
              Körningen är klar – granska innan materialet blir aktivt
            </Typography>
            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, maxWidth: 820, mt: 0.5 }}>
              Fullkörningen skapade redaktionellt underlag, men inget publiceras automatiskt. Följ stegen nedan för att granska artiklar, hantera discovery och göra rapporten aktiv.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, justifyContent: { md: 'flex-end' } }}>
            <Chip size="small" color="success" label="Completed" />
            <Chip size="small" variant="outlined" label={getRunCategoryModeLabel(run.categoryRunMode)} />
            <Chip size="small" variant="outlined" label={formatDateTime(run.finishedAt || run.updatedAt || run.startedAt)} />
            <Chip size="small" color={reportIsPublished ? 'success' : 'warning'} variant={reportIsPublished ? 'filled' : 'outlined'} label={reportStatusLabel(summary.report)} />
          </Stack>
        </Stack>

        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} sx={{ color: TREND_COLORS.navy }} />
            <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>
              Läser in arbetskön efter körningen...
            </Typography>
          </Stack>
        )}
        {error && <Alert severity="warning">{error}</Alert>}

        <Grid container spacing={1.5}>
          <ActionCard
            icon={<FactCheckOutlinedIcon />}
            title="Granska artiklar"
            chip={canReview ? `${articleCounts.review} review · ${articleCounts.blocked} blockerade` : 'Kräver review-rättighet'}
            tone={canReview ? actionTone(articleCounts.review + articleCounts.blocked, 'success') : 'info'}
            description={canReview
              ? `${articleCounts.publishable} artiklar från körningen ser publicerbara ut. Kontrollera review och blockerade artiklar i den redaktionella arbetsbänken.`
              : 'Artiklar som kräver review hanteras i Redaktionellt av användare med granskningsrättighet.'}
            buttonLabel="Öppna Redaktionellt"
            onClick={canReview && onNavigateModule ? () => onNavigateModule('editorial') : undefined}
          />
          <ActionCard
            icon={<TravelExploreOutlinedIcon />}
            title="Hantera discovery-kandidater"
            chip={`${summary.pendingDiscoveryCount} redo · ${summary.needsValidationDiscoveryCount} validering`}
            tone={actionTone(discoveryQueueCount, 'success')}
            description="Godkänn, importera, avvisa eller lägg discovery-kandidater i karantän innan nästa fulla körning använder dem."
            buttonLabel="Öppna Discovery"
            onClick={canManageSources ? () => onControlCenterTab(2) : undefined}
            disabled={!canManageSources}
          />
          <ActionCard
            icon={<ArticleOutlinedIcon />}
            title="Kontrollera rapportutkast"
            chip={reportStatusLabel(summary.report)}
            tone={reportIsPublished ? 'success' : 'warning'}
            description={summary.report
              ? `Rapporten "${summary.report.title}" är ${reportStatusLabel(summary.report).toLowerCase()}. Kontrollera sammanfattning och urval innan den används som aktiv trendbild.`
              : 'Körningen markerar ett rapport-id, men rapporten kunde inte laddas. Kontrollera rapportlistan i Redaktionellt.'}
            buttonLabel={reportIsPublished ? 'Öppna Rapportarkiv' : 'Öppna Redaktionellt'}
            onClick={reportIsPublished
              ? () => onControlCenterTab(6)
              : canReview && onNavigateModule ? () => onNavigateModule('editorial') : undefined}
            disabled={!reportIsPublished && !canReview}
          />
          <ActionCard
            icon={<PublishOutlinedIcon />}
            title="Gör rapporten aktiv"
            chip={reportIsPublished ? 'Redan aktiv' : 'Manuellt steg'}
            tone={reportIsPublished ? 'success' : 'warning'}
            description={reportIsPublished
              ? 'Rapporten är redan aktiv i trendmodulen och kan läsas i arkivet och på översikten.'
              : 'När review är klar görs rapporten aktiv från Redaktionellt. Det är först då rapportartiklar publiceras.'}
            buttonLabel={reportIsPublished ? 'Visa översikt' : 'Gå till publicering'}
            onClick={reportIsPublished
              ? onNavigateModule ? () => onNavigateModule('overview') : undefined
              : canReview && onNavigateModule ? () => onNavigateModule('editorial') : undefined}
            disabled={!reportIsPublished && !canReview}
          />
          <ActionCard
            icon={<LibraryBooksOutlinedIcon />}
            title="Visa nytt material"
            chip="Översikt"
            tone="info"
            description="När rapporten är aktiv kan du använda översiktens filter för att se vad som är nytt, uppdaterat eller återanvänt från senaste körningen."
            buttonLabel="Öppna Översikt"
            onClick={onNavigateModule ? () => onNavigateModule('overview') : undefined}
          />
          <ActionCard
            icon={<WarningAmberOutlinedIcon />}
            title="Åtgärda källproblem"
            chip={`${summary.sourceReviewCount} review · ${summary.sourceFailedCount} fel`}
            tone={actionTone(sourceIssueCount, 'success')}
            description="Källposter som kräver granskning eller har misslyckats behöver kontrolleras i Hämtat material innan de används vidare."
            buttonLabel="Öppna Hämtat material"
            onClick={canManageSources ? () => onControlCenterTab(4) : undefined}
            disabled={!canManageSources}
          />
        </Grid>

        {run.notes.length > 0 && (
          <Alert severity="info" icon={<AssignmentTurnedInOutlinedIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Körningsnoteringar
            </Typography>
            <Typography variant="body2">
              {run.notes.slice(0, 3).join(' · ')}
            </Typography>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
